import type { InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { getSession } from './supabase';

// Create axios instance
export const api = axios.create({
  baseURL: `https://${projectId}.supabase.co/functions/v1/make-server-11b63af5`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// List of endpoints that don't require authentication
const publicEndpoints = ['/signup', '/metadata', '/public/'];

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Check if endpoint requires authentication
      const isPublicEndpoint = publicEndpoints.some((endpoint) => config.url?.includes(endpoint));

      if (isPublicEndpoint) {
        // For public endpoints, use the anon key instead of user token
        config.headers.Authorization = `Bearer ${publicAnonKey}`;
      } else {
        // For protected endpoints, use user session token
        const session = await getSession();
        if (session?.access_token) {
          config.headers.Authorization = `Bearer ${session.access_token}`;
        } else {
          // If no session and not public endpoint, use anon key as fallback
          config.headers.Authorization = `Bearer ${publicAnonKey}`;
        }
      }
    } catch (error) {
      console.error('Error getting session in axios interceptor:', error);
      // Fallback to anon key if error occurs
      config.headers.Authorization = `Bearer ${publicAnonKey}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.data);
    } else if (error.request) {
      // Request was made but no response
      console.error('Network Error:', error.message);
    } else {
      // Something else happened
      console.error('Request Error:', error.message);
    }
    return Promise.reject(error);
  }
);
